// When cpq2 is launched from the "Edit Config" button, NetSuite passes the
// line's item on the URL (?itemid=). On mount we fetch that item's saved
// configuration and apply it — the same path as the manual "Item Template"
// Load, run automatically so Edit Config opens on the existing configuration.
//
// Renders nothing until it has a status to show; then a small fixed banner.

import { useEffect, useRef, useState } from "react";
import { useItemLoader } from "../state/useItemLoader";
import { useAnswers } from "../state/AnswersContext";
import { getLaunchContext } from "../util/launchContext";

type Status = { kind: "ok" | "err" | "info"; text: string };

// `onDone` fires exactly once when the preload has settled (loaded, errored, or
// there was nothing to load) — Configurator uses it to reveal the page only
// after the saved configuration is applied, so Edit Config never flashes an
// empty/default form that then repopulates a beat later.
export default function AutoLoadItem({ onDone }: { onDone?: () => void }) {
  const loadItem = useItemLoader();
  const { forceNewConfig, noteLockedItem } = useAnswers();
  const [status, setStatus] = useState<Status | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const itemId = getLaunchContext().itemId;
    if (!itemId) {
      onDone?.(); // "Configure" (new line) — nothing to preload.
      return;
    }

    let cancelled = false;
    setStatus({ kind: "info", text: "Loading saved configuration…" });
    loadItem(itemId)
      .then((res) => {
        if (cancelled) return;
        // The launched item is flagged "can't edit via CPQ": keep its config
        // loaded as a starting point, but switch to New Config so Save & Submit
        // makes a NEW part instead of editing this locked item.
        if (res.cannotEdit) {
          forceNewConfig();
          // Remember WHICH item was refused, so the admin override has something to
          // re-enter Edit mode on. Without this the override would know a code is valid
          // but not what to apply it to.
          noteLockedItem(String(itemId));
          setStatus({
            kind: "info",
            text: `${res.text} · This item can't be edited via CPQ${res.lockReason ? ` (${res.lockReason})` : ""} — Save & Submit will create a NEW part.`,
          });
          return;
        }
        setStatus(res);
      })
      .catch((e) => {
        if (!cancelled) setStatus({ kind: "err", text: e instanceof Error ? e.message : String(e) });
      })
      .finally(() => {
        // Reveal the page whether the load succeeded or failed — never trap the
        // user behind the preload overlay.
        if (!cancelled) onDone?.();
      });
    return () => {
      cancelled = true;
    };
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss the success banner so it doesn't sit on top of the toolbar
  // buttons. Errors (and the mapping-fallback notice) stay until clicked so
  // they aren't missed.
  useEffect(() => {
    if (status?.kind !== "ok") return;
    const t = window.setTimeout(() => setStatus(null), 6000);
    return () => window.clearTimeout(t);
  }, [status]);

  if (!status) return null;
  return (
    <div
      className={`cpq-item-template-status is-${status.kind}`}
      // Pinned to the BOTTOM-right so it never covers the Cancel / Save & Submit
      // buttons in the top-right of the header. Click to dismiss.
      style={{ position: "fixed", bottom: 12, right: 12, zIndex: 9999, maxWidth: 460, cursor: "pointer" }}
      role="status"
      onClick={() => setStatus(null)}
      title="Dismiss"
    >
      {status.text}
    </div>
  );
}
